import React, { useEffect, useState } from "react";
import { History, ChevronDown, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * v79.193 — Admin edit audit log for a single listing.
 *
 * Founder direction (Feb 2026): "Admin edit audit log (who edited
 * what & when) so sellers see a change history on their listing —
 * improves trust + dispute resolution."
 *
 * Visibility:
 *   - Listing owner sees their own audit history
 *   - Admins see it on every listing
 *   - Everyone else gets 403 from the API (no leaks)
 *
 * Renders as a collapsed accordion by default (so listings without
 * admin edits stay quiet) — expands to show a per-field diff timeline
 * (newest first) with the admin's name + timestamp.
 */
export default function AdminAuditLog({ listingId, canView }) {
    const [audits, setAudits] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!canView || !listingId || loaded) return;
        setLoading(true);
        api.get(`/listings/${listingId}/admin-audits`)
            .then((r) => setAudits(r.data.items || []))
            .catch((e) => {
                // 403 = not the owner and not an admin. Silent.
                if (e?.response?.status !== 403) toast.error(formatApiError(e));
            })
            .finally(() => { setLoading(false); setLoaded(true); });
    }, [listingId, canView, loaded]);

    if (!canView || audits.length === 0) return null;

    return (
        <div data-testid="admin-audit-log" className="mt-6 rounded-lg border border-aduku-border bg-aduku-bg/60 overflow-hidden">
            <button
                data-testid="admin-audit-log-toggle"
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-aduku-bg transition"
            >
                <span className="flex items-center gap-2">
                    <History className="w-4 h-4 text-aduku-navy" />
                    <span className="font-display font-bold text-sm text-aduku-navy">Change history</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-aduku-yellow text-aduku-navy text-[10px] font-black uppercase tracking-wider">
                        {audits.length} admin edit{audits.length === 1 ? "" : "s"}
                    </span>
                </span>
                <ChevronDown className={`w-4 h-4 text-aduku-navy/60 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="border-t border-aduku-border px-4 py-3 space-y-4 max-h-[420px] overflow-y-auto">
                    {loading && <div className="text-xs text-aduku-navy/60">Loading history…</div>}
                    {audits.map((entry) => (
                        <AuditEntry key={entry.id} entry={entry} />
                    ))}
                </div>
            )}
        </div>
    );
}

function AuditEntry({ entry }) {
    const when = entry.created_at ? new Date(entry.created_at) : null;
    const whenStr = when ? when.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";
    return (
        <div data-testid={`audit-entry-${entry.id}`} className="rounded-md border border-aduku-border bg-white p-3">
            <div className="flex items-center gap-2 text-xs">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                <span className="font-bold text-aduku-navy">{entry.admin_name || entry.admin_email || "Admin"}</span>
                <span className="text-aduku-navy/50">·</span>
                <span className="text-aduku-navy/70">{whenStr}</span>
            </div>
            <ul className="mt-2 space-y-1.5">
                {(entry.changes || []).map((c, i) => (
                    <li key={`${entry.id}-${c.field}-${i}`} className="text-[12px] leading-relaxed">
                        <span className="font-bold text-aduku-navy uppercase tracking-wider text-[10px]">{c.field}</span>
                        <div className="flex flex-wrap items-baseline gap-1.5">
                            <ValueChip value={c.before} tone="before" />
                            <span className="text-aduku-navy/50">→</span>
                            <ValueChip value={c.after} tone="after" />
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function ValueChip({ value, tone }) {
    const isEmpty = value === null || value === undefined || value === "";
    const display = isEmpty
        ? "—"
        : (typeof value === "object" ? JSON.stringify(value) : String(value));
    const cls = tone === "after"
        ? "bg-emerald-50 text-emerald-800 border-emerald-200"
        : "bg-red-50 text-red-800 border-red-200 line-through decoration-red-400/60";
    return (
        <span className={`inline-block max-w-[220px] truncate px-2 py-0.5 rounded border text-[11px] ${cls}`} title={display}>
            {display}
        </span>
    );
}
